import { ColorHSL, ColorRGBA64, hslToRGB, rgbToRelativeLuminance } from "@microsoft/fast-colors";
import { RelativeLuminance } from "./luminance";
import { Luminance } from "./recipe";

/**
 * Represents a color in a {@link Palette}.
 */
export interface Swatch extends RelativeLuminance {
    toColorString(): string;
    contrast(target: RelativeLuminance): number;
}

/**
 * A concrete implementation of {@link Swatch} with red, green, and blue 64bit color channels.
 * */
export class SwatchRGB extends ColorRGBA64 implements Swatch {
    readonly relativeLuminance: number;
    r: number;
    g: number;
    b: number;

    /**
    *
    * @param red - Red channel expressed as a number between 0 and 1
    * @param green - Green channel expressed as a number between 0 and 1
    * @param blue - Blue channel expressed as a number between 0 and 1
    */
    constructor(red: number, green: number, blue: number) {
        super(red, green, blue, 1);
        this.relativeLuminance = rgbToRelativeLuminance(this);
    }

    public toColorString = this.toStringHexRGB;
    public contrast = Luminance.getContrast.bind(null, this);
    public createCSS = this.toColorString;

    static fromObject(obj: { r: number; g: number; b: number }): SwatchRGB {
        return new SwatchRGB(obj.r, obj.g, obj.b);
    }
}

/**
 * A concrete implementation of {@link Swatch} for HSL.
 * */
export class SwatchHSL extends ColorHSL implements Swatch {
    readonly relativeLuminance: number;
    h: number;
    s: number;
    l: number;

    /**
    *
    * @param hue - Hue channel expressed as a number between 0 and 1
    * @param saturation - Saturation channel expressed as a number between 0 and 1
    * @param lightness - Lightness channel expressed as a number between 0 and 1
    */
    constructor(hue: number, saturation: number, lightness: number) {
        super(hue, saturation, lightness);
        // TODO: Is this correct?
        this.relativeLuminance = lightness;
    }

    public toColorString = () => hslToRGB(this).toStringHexRGB();
    public contrast = Luminance.getContrast.bind(null, this);
    public createCSS = this.toColorString;

    static fromObject(obj: { h: number, s: number, l: number }): SwatchHSL {
        return new SwatchHSL(obj.h, obj.s, obj.l);
    }
}

export const white = new SwatchRGB(1, 1, 1);
export const black = new SwatchRGB(0, 0, 0);
